import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { RefreshCw, Search, Download, MessageSquare, Clock, Radio, Loader2, X, CheckCircle2, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { playerProfileService } from '../apis/playerProfileService';
import BulkSmsMessaging from '../components/sms/BulkSmsMessaging';
import ScheduledSmsModal from '../components/PlayerProfile/ScheduledSmsModal';

const DEFAULT_LIMIT = 50;
const CST_TIMEZONE = 'America/Chicago';

const REASON_LABELS = {
  invalid_format: 'Invalid format',
  invalid_number: 'Invalid / undeliverable',
  unsubscribed: 'Unsubscribed',
};

const logStatus = (row) => {
  if (row?.send_type === 'inbound') return 'inbound';
  if (row?.twilio_status === 'simulated_local') return 'simulated';
  if (row?.success) return 'sent';
  if (row?.twilio_status === 'skipped' || String(row?.twilio_status).startsWith('suppressed')) return 'skipped';
  return 'failed';
};

const statusBadge = (status) => {
  if (status === 'inbound') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
        Received
      </span>
    );
  }
  if (status === 'simulated') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        Simulated (Dev)
      </span>
    );
  }
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Sent
      </span>
    );
  }
  if (status === 'skipped') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
        Skipped
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      Failed
    </span>
  );
};

const reasonBadge = (reason) => {
  const label = REASON_LABELS[reason] || reason || 'Unknown';
  const cls =
    reason === 'unsubscribed'
      ? 'bg-gray-100 text-gray-800'
      : reason === 'invalid_format'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-red-100 text-red-800';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
};

const escapeCsv = (v) => {
  if (v == null) return '';
  const s = String(v).replace(/"/g, '""');
  return /[,"\n\r]/.test(s) ? `"${s}"` : s;
};

const downloadCsv = (filename, headers, rows) => {
  const csv = '\uFEFF' + [headers.join(','), ...rows].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const Pagination = ({ page, totalPages, total, loading, onPage }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
    <div className="text-sm text-gray-600">
      Total: <span className="font-medium">{total}</span>
    </div>
    <div className="flex items-center gap-2">
      <button
        onClick={() => onPage(Math.max(1, page - 1))}
        disabled={page <= 1 || loading}
        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Prev
      </button>
      <div className="text-sm text-gray-700">
        Page <span className="font-medium">{page}</span> / <span className="font-medium">{totalPages}</span>
      </div>
      <button
        onClick={() => onPage(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages || loading}
        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Next
      </button>
    </div>
  </div>
);

const SmsLogsPage = () => {
  const [tab, setTab] = useState('activity');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ sent: 0, inbound: 0, skipped: 0, failed: 0, total: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [unblockingId, setUnblockingId] = useState(null);
  const [unblockConfirmTarget, setUnblockConfirmTarget] = useState(null);

  const [invalidRows, setInvalidRows] = useState([]);
  const [invalidLoading, setInvalidLoading] = useState(false);
  const [invalidError, setInvalidError] = useState(null);
  const [invalidPage, setInvalidPage] = useState(1);
  const [invalidLimit, setInvalidLimit] = useState(DEFAULT_LIMIT);
  const [invalidTotalPages, setInvalidTotalPages] = useState(1);
  const [invalidTotal, setInvalidTotal] = useState(0);
  const [invalidSummary, setInvalidSummary] = useState({ unique_numbers: 0, total_hits: 0, by_reason: {} });
  const [invalidSearch, setInvalidSearch] = useState('');
  const [invalidReason, setInvalidReason] = useState('all');
  const [invalidSort, setInvalidSort] = useState('newest');
  const [invalidFrom, setInvalidFrom] = useState('');
  const [invalidTo, setInvalidTo] = useState('');

  // Messaging & Scheduling Modal State
  const [showBulkMessagingModal, setShowBulkMessagingModal] = useState(false);
  const [showScheduledSmsModal, setShowScheduledSmsModal] = useState(false);
  const [scheduledInitialMessage, setScheduledInitialMessage] = useState('');
  const [scheduledRecipients, setScheduledRecipients] = useState([]);
  const [scheduledTargetType, setScheduledTargetType] = useState('all_active');

  // Load distinct players for the messaging modal
  const loadSmsPlayers = useCallback(async () => {
    try {
      const res = await playerProfileService.getDistinctPlayers(1, 10000, '');
      return res?.data || [];
    } catch (e) {
      console.error('Failed to load players for messaging:', e);
      return [];
    }
  }, []);

  const [activePlayerCount, setActivePlayerCount] = useState(null);

  // Gateway Balance State (Twilio & Telnyx)
  const [gatewayBalances, setGatewayBalances] = useState({});
  const [balanceLoading, setBalanceLoading] = useState(false);

  const fetchSmsBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const res = await playerProfileService.getSmsBalance();
      const payload = res?.data?.data || res?.data;
      if (payload?.providers) {
        setGatewayBalances(payload.providers);
      }
      if (payload?.activeProvider) {
        setActivePlatform((prev) => prev || payload.activeProvider);
      }
    } catch (e) {
      console.warn('Failed to fetch SMS gateway balance:', e);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSmsBalance();
  }, [fetchSmsBalance]);

  // Active SMS Gateway Platform (Twilio / Telnyx)
  const [activePlatform, setActivePlatform] = useState('twilio');
  const [switchingPlatform, setSwitchingPlatform] = useState(false);

  useEffect(() => {
    playerProfileService.getSmsProvider().then((res) => {
      const current = res?.data?.data?.activeProvider;
      if (current) setActivePlatform(current);
    }).catch(() => {});
  }, []);

  const handleSwitchPlatform = async (newPlatform) => {
    if (newPlatform === activePlatform) return;
    setSwitchingPlatform(true);
    try {
      const res = await playerProfileService.setSmsProvider(newPlatform);
      if (res?.success) {
        setActivePlatform(newPlatform);
        toast.success(`Active SMS platform switched to ${newPlatform.toUpperCase()}`);
        fetchSmsBalance();
      } else {
        toast.error(res?.error || 'Failed to switch SMS platform');
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to switch SMS platform');
    } finally {
      setSwitchingPlatform(false);
    }
  };

  useEffect(() => {
    playerProfileService.getActivePlayersPhones().then((res) => {
      const count = res?.data?.data?.count ?? res?.data?.data?.phoneNumbers?.length ?? null;
      if (count != null) setActivePlayerCount(count);
    }).catch(() => {});
  }, []);

  // Send to all active players configuration
  const sendAllActiveConfig = useMemo(() => ({
    label: 'Send to All Active Players',
    buttonLabel: 'Send to Active Players',
    description: 'Send this message to all currently active club players with phone numbers',
    count: activePlayerCount,
    fetchRecipients: async () => {
      const res = await playerProfileService.getActivePlayersPhones();
      return res?.data?.data || { players: [], phoneNumbers: [] };
    }
  }), [activePlayerCount]);

  const logQuery = useMemo(() => {
    const p = { page, limit, sort };
    if (search.trim()) p.search = search.trim();
    if (statusFilter === 'sent') p.status = 'sent';
    if (statusFilter === 'inbound') p.status = 'inbound';
    if (statusFilter === 'skipped') p.status = 'skipped';
    if (statusFilter === 'failed') p.status = 'failed';
    if (dateFrom) p.from = moment.tz(dateFrom, 'YYYY-MM-DD', CST_TIMEZONE).startOf('day').toISOString();
    if (dateTo) p.to = moment.tz(dateTo, 'YYYY-MM-DD', CST_TIMEZONE).endOf('day').toISOString();
    return p;
  }, [page, limit, sort, search, statusFilter, dateFrom, dateTo]);

  const invalidQuery = useMemo(() => {
    const p = { page: invalidPage, limit: invalidLimit, sort: invalidSort };
    if (invalidSearch.trim()) p.search = invalidSearch.trim();
    if (invalidReason !== 'all') p.reason = invalidReason;
    if (invalidFrom) p.from = moment.tz(invalidFrom, 'YYYY-MM-DD', CST_TIMEZONE).startOf('day').toISOString();
    if (invalidTo) p.to = moment.tz(invalidTo, 'YYYY-MM-DD', CST_TIMEZONE).endOf('day').toISOString();
    return p;
  }, [invalidPage, invalidLimit, invalidSort, invalidSearch, invalidReason, invalidFrom, invalidTo]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await playerProfileService.getSmsLogs(logQuery);
      if (!response?.success) {
        setError(response?.error || 'Failed to load SMS logs');
        setRows([]);
        return;
      }
      const data = response?.data?.data || {};
      setRows(data.rows || []);
      const pagination = data.pagination || {};
      setTotal(pagination.total || 0);
      setTotalPages(pagination.totalPages || 1);
      setSummary({
        sent: data.summary?.sent || 0,
        inbound: data.summary?.inbound || 0,
        skipped: data.summary?.skipped || 0,
        failed: data.summary?.failed || 0,
        total: data.summary?.total || pagination.total || 0,
      });
    } catch (e) {
      setError(e?.message || 'Failed to load SMS logs');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [logQuery]);

  const fetchInvalid = useCallback(async () => {
    setInvalidLoading(true);
    setInvalidError(null);
    try {
      const response = await playerProfileService.getSmsInvalidPhones(invalidQuery);
      if (!response?.success) {
        setInvalidError(response?.error || 'Failed to load invalid numbers');
        setInvalidRows([]);
        return;
      }
      const data = response?.data?.data || {};
      setInvalidRows(data.rows || []);
      const pagination = data.pagination || {};
      setInvalidTotal(pagination.total || 0);
      setInvalidTotalPages(pagination.totalPages || 1);
      setInvalidSummary({
        unique_numbers: data.summary?.unique_numbers || 0,
        total_hits: data.summary?.total_hits || 0,
        by_reason: data.summary?.by_reason || {},
      });
    } catch (e) {
      setInvalidError(e?.message || 'Failed to load invalid numbers');
      setInvalidRows([]);
    } finally {
      setInvalidLoading(false);
    }
  }, [invalidQuery]);

  const handleUnblock = useCallback((row) => {
    setUnblockConfirmTarget(row);
  }, []);

  const confirmUnblock = useCallback(async () => {
    if (!unblockConfirmTarget) return;
    const row = unblockConfirmTarget;
    setUnblockingId(row.id);
    try {
      const res = await playerProfileService.unblockInvalidPhone(row.id);
      if (res?.success) {
        toast.success(`Unblocked ${row.phone}. Player can now receive SMS.`);
        setUnblockConfirmTarget(null);
        fetchInvalid();
        fetchLogs();
      } else {
        toast.error(res?.error || 'Failed to unblock phone');
      }
    } catch (e) {
      toast.error(e?.message || 'Error unblocking phone');
    } finally {
      setUnblockingId(null);
    }
  }, [unblockConfirmTarget, fetchInvalid, fetchLogs]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchInvalid();
  }, [fetchInvalid]);

  const downloadLogReport = useCallback(async () => {
    try {
      const response = await playerProfileService.getSmsLogs({ ...logQuery, page: 1, limit: 5000, export: '1' });
      if (!response?.success) return;
      const exportRows = response?.data?.data?.rows || [];
      downloadCsv(
        `sms-logs-${moment().tz(CST_TIMEZONE).format('YYYY-MM-DD-HHmm')}.csv`,
        ['Time (CST)', 'Player Name', 'Player Number', 'Player ID', 'Phone', 'Status', 'Sent By', 'Message', 'Error'],
        exportRows.map((row) =>
          [
            row.created_at ? moment(row.created_at).tz(CST_TIMEZONE).format('YYYY-MM-DD HH:mm:ss') : '',
            escapeCsv(row.player_name),
            escapeCsv(row.player_number),
            row.player_id != null ? row.player_id : '',
            escapeCsv(row.phone),
            logStatus(row) === 'sent' ? 'Sent' : logStatus(row) === 'skipped' ? 'Skipped' : 'Failed',
            escapeCsv(row.sent_by_email),
            escapeCsv(row.message),
            escapeCsv(row.error),
          ].join(',')
        )
      );
    } catch (e) {
      console.error('Download failed:', e);
    }
  }, [logQuery]);

  const downloadInvalidReport = useCallback(async () => {
    try {
      const response = await playerProfileService.getSmsInvalidPhones({
        ...invalidQuery,
        page: 1,
        limit: 5000,
        export: '1',
      });
      if (!response?.success) return;
      const exportRows = response?.data?.data?.rows || [];
      downloadCsv(
        `sms-invalid-phones-${moment().tz(CST_TIMEZONE).format('YYYY-MM-DD-HHmm')}.csv`,
        ['Phone', 'Original', 'Player Name', 'Player Number', 'Reason', 'Error', 'Times rejected', 'First seen (CST)', 'Last seen (CST)', 'Last sent by'],
        exportRows.map((row) =>
          [
            escapeCsv(row.phone),
            escapeCsv(row.original_phone),
            escapeCsv(row.player_name),
            escapeCsv(row.player_number),
            escapeCsv(REASON_LABELS[row.reason] || row.reason),
            escapeCsv(row.error_message),
            row.hit_count || 0,
            row.first_seen_at ? moment(row.first_seen_at).tz(CST_TIMEZONE).format('YYYY-MM-DD HH:mm:ss') : '',
            row.last_seen_at ? moment(row.last_seen_at).tz(CST_TIMEZONE).format('YYYY-MM-DD HH:mm:ss') : '',
            escapeCsv(row.last_sent_by_email),
          ].join(',')
        )
      );
    } catch (e) {
      console.error('Download failed:', e);
    }
  }, [invalidQuery]);

  const currentBalance = gatewayBalances[activePlatform?.toLowerCase()] || gatewayBalances[activePlatform] || null;

  const summaryCards = [
    { key: 'sent', label: 'Sent (Outbound)', value: summary.sent || 0, active: 'bg-green-50 border-green-200 text-green-800', idle: 'bg-white' },
    { key: 'inbound', label: 'Inbound replies', value: summary.inbound || 0, active: 'bg-purple-50 border-purple-200 text-purple-800', idle: 'bg-white' },
    { key: 'skipped', label: 'Skipped / invalid', value: summary.skipped || 0, active: 'bg-amber-50 border-amber-200 text-amber-800', idle: 'bg-white' },
    { key: 'failed', label: 'Failed', value: summary.failed || 0, active: 'bg-red-50 border-red-200 text-red-800', idle: 'bg-white' },
    { key: 'all', label: 'All messages', value: summary.total || 0, active: 'bg-blue-50 border-blue-200 text-blue-800', idle: 'bg-white' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="bg-white shadow-sm border-b border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SMS</h1>
            <p className="text-sm text-gray-500 mt-1">
              Send announcements, manage scheduled campaigns, and track real-time delivery reports.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Gateway Platform Switcher */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200/80 border border-gray-300 rounded-lg text-xs transition-colors shadow-sm">
              <Radio size={14} className="text-emerald-600 animate-pulse" />
              <span className="font-semibold text-gray-500">Gateway:</span>
              <select
                value={activePlatform}
                onChange={(e) => handleSwitchPlatform(e.target.value)}
                disabled={switchingPlatform}
                className="font-bold text-gray-900 bg-transparent focus:outline-none cursor-pointer"
                title="Switch active SMS gateway platform (Twilio / Telnyx)"
              >
                <option value="twilio">Twilio (Default)</option>
                <option value="telnyx">Telnyx</option>
              </select>
              {switchingPlatform && <Loader2 size={13} className="animate-spin text-amber-600" />}
            </div>

            {/* Real-time SMS Gateway Balance */}
            <div
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs transition-colors shadow-sm ${
                balanceLoading
                  ? 'bg-gray-100 border-gray-300 text-gray-500'
                  : currentBalance?.balance != null
                  ? currentBalance.balance < 5
                    ? 'bg-amber-50 border-amber-300 text-amber-900'
                    : 'bg-emerald-50 border-emerald-300 text-emerald-900'
                  : 'bg-gray-100 border-gray-300 text-gray-600'
              }`}
              title={
                currentBalance?.balance != null
                  ? `${activePlatform.toUpperCase()} Balance: $${Number(currentBalance.balance).toFixed(2)} ${currentBalance.currency || 'USD'}${
                      currentBalance.availableCredit != null ? ` (Available Credit: $${Number(currentBalance.availableCredit).toFixed(2)})` : ''
                    }${currentBalance.isMock ? ' [Mock Mode]' : ''}`
                  : currentBalance?.error || `${activePlatform.toUpperCase()} balance unavailable`
              }
            >
              <Wallet size={14} className={currentBalance?.balance != null && currentBalance.balance < 5 ? 'text-amber-600' : 'text-emerald-600'} />
              <span className="font-semibold text-gray-500">Balance:</span>
              {balanceLoading ? (
                <span className="inline-flex items-center gap-1 text-gray-500 font-medium">
                  <Loader2 size={11} className="animate-spin text-amber-600" />
                  Loading...
                </span>
              ) : currentBalance?.balance != null ? (
                <span className="font-bold flex items-center gap-1">
                  <span>${Number(currentBalance.balance).toFixed(2)}</span>
                  <span className="text-[10px] font-medium opacity-75">{currentBalance.currency || 'USD'}</span>
                  {currentBalance.isMock && (
                    <span className="text-[9px] px-1 py-0.2 bg-blue-100 text-blue-700 rounded font-normal">mock</span>
                  )}
                  {currentBalance.balance < 5 && (
                    <span className="text-[9px] px-1 py-0.2 bg-amber-200 text-amber-800 rounded font-bold">LOW</span>
                  )}
                </span>
              ) : currentBalance?.configured === false ? (
                <span className="italic text-gray-400">Not configured</span>
              ) : (
                <span className="text-gray-400 italic">--</span>
              )}
              <button
                type="button"
                onClick={fetchSmsBalance}
                disabled={balanceLoading}
                className="ml-1 p-0.5 text-gray-400 hover:text-gray-800 hover:bg-gray-200/60 rounded disabled:opacity-40 transition-colors"
                title="Refresh gateway balance"
              >
                <RefreshCw size={11} className={balanceLoading ? 'animate-spin' : ''} />
              </button>
            </div>

            <button
              onClick={() => setShowBulkMessagingModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-950 bg-amber-400 rounded-md hover:bg-amber-500 border border-amber-500 shadow-sm transition-colors"
              title="Compose and send text messages to players"
            >
              <MessageSquare size={16} className="text-gray-950" />
              Send SMS
            </button>
            <button
              onClick={() => setShowScheduledSmsModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-amber-950 bg-amber-100 border border-amber-300 rounded-md hover:bg-amber-200 shadow-sm transition-colors"
              title="Manage automated and scheduled text messages"
            >
              <Clock size={16} className="text-amber-900" />
              Scheduled SMS
            </button>
            <button
              onClick={tab === 'invalid' ? downloadInvalidReport : downloadLogReport}
              disabled={tab === 'invalid' ? invalidLoading : loading}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Download size={15} />
              Download report
            </button>
            <button
              onClick={tab === 'invalid' ? fetchInvalid : fetchLogs}
              disabled={tab === 'invalid' ? invalidLoading : loading}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <RefreshCw size={15} className={(tab === 'invalid' ? invalidLoading : loading) ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => setTab('activity')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              tab === 'activity' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Message activity
          </button>
          <button
            type="button"
            onClick={() => setTab('invalid')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              tab === 'invalid' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Invalid numbers
            {invalidSummary.unique_numbers > 0 ? ` (${invalidSummary.unique_numbers})` : ''}
          </button>
        </div>
      </div>

      {tab === 'activity' ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {summaryCards.map((card) => (
              <button
                key={card.key}
                type="button"
                onClick={() => {
                  setStatusFilter(card.key);
                  setPage(1);
                }}
                className={`text-left border rounded-lg p-4 transition-colors ${
                  statusFilter === card.key ? card.active : `${card.idle} border-gray-200 hover:border-gray-300`
                }`}
              >
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{card.label}</p>
                <p className="text-2xl font-bold mt-1">{card.value}</p>
              </button>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex flex-col lg:flex-row gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search by phone, player, sent by, message..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                min={dateFrom || undefined}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                  setPage(1);
                }}
                className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Clear dates
              </button>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
              >
                <option value="all">All statuses</option>
                <option value="sent">Sent (Outbound)</option>
                <option value="inbound">Inbound (Replies)</option>
                <option value="skipped">Skipped / invalid</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
              >
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>

            {error && <div className="px-4 py-3 text-sm text-red-600">{error}</div>}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Time (CST)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Direction</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Player</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Source / Sent By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Message</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading && rows.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-10 text-center text-sm text-gray-500">No SMS logs found.</td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {row.created_at ? (
                            <div title={`${moment(row.created_at).tz(CST_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')} CST`}>
                              <div className="font-medium text-gray-900">
                                {moment(row.created_at).tz(CST_TIMEZONE).format('MMM DD, YYYY')}
                              </div>
                              <div className="text-xs text-gray-500 font-mono">
                                {moment(row.created_at).tz(CST_TIMEZONE).format('h:mm A')} CST
                              </div>
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {row.send_type === 'inbound' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                              📥 Inbound
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                              📤 Outbound
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="font-medium">{row.player_name || '—'}</div>
                          <div className="text-xs text-gray-500">
                            {row.player_number ? `#${row.player_number}` : ''}
                            {row.player_id ? ` • ID ${row.player_id}` : ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{row.phone}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">{statusBadge(logStatus(row))}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {row.send_type === 'inbound' ? (
                            <span className="text-purple-700 font-medium">Inbound SMS</span>
                          ) : (
                            row.sent_by_email || '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[520px]">
                          <div className="line-clamp-2 whitespace-pre-wrap">{row.message || ''}</div>
                          {!row.success && row.error && (
                            <div className="mt-1 text-xs text-red-600 line-clamp-2">{row.error}</div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} loading={loading} onPage={setPage} />
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Unique numbers</p>
              <p className="text-2xl font-bold mt-1">{invalidSummary.unique_numbers}</p>
            </div>
            <div className="border border-amber-200 rounded-lg p-4 bg-amber-50">
              <p className="text-xs font-medium uppercase tracking-wider text-amber-700">Times skipped</p>
              <p className="text-2xl font-bold mt-1 text-amber-800">{invalidSummary.total_hits}</p>
            </div>
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <p className="text-xs font-medium uppercase tracking-wider text-red-700">Invalid / undeliverable</p>
              <p className="text-2xl font-bold mt-1 text-red-800">
                {(invalidSummary.by_reason.invalid_number || 0) + (invalidSummary.by_reason.invalid_format || 0)}
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Unsubscribed</p>
              <p className="text-2xl font-bold mt-1">{invalidSummary.by_reason.unsubscribed || 0}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <p className="text-sm text-gray-600 mb-3">
                Each phone is stored once. Last seen and times rejected update when SMS is skipped again — use this list to fix player records.
              </p>
              <div className="flex flex-col lg:flex-row gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px] relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    value={invalidSearch}
                    onChange={(e) => {
                      setInvalidSearch(e.target.value);
                      setInvalidPage(1);
                    }}
                    placeholder="Search phone, player, or rejection reason..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <input
                  type="date"
                  value={invalidFrom}
                  onChange={(e) => {
                    setInvalidFrom(e.target.value);
                    setInvalidPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                />
                <input
                  type="date"
                  value={invalidTo}
                  onChange={(e) => {
                    setInvalidTo(e.target.value);
                    setInvalidPage(1);
                  }}
                  min={invalidFrom || undefined}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                />
                <select
                  value={invalidReason}
                  onChange={(e) => {
                    setInvalidReason(e.target.value);
                    setInvalidPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                >
                  <option value="all">All reasons</option>
                  <option value="invalid_format">Invalid format</option>
                  <option value="invalid_number">Invalid / undeliverable</option>
                  <option value="unsubscribed">Unsubscribed</option>
                </select>
                <select
                  value={invalidSort}
                  onChange={(e) => {
                    setInvalidSort(e.target.value);
                    setInvalidPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                >
                  <option value="newest">Newest last seen</option>
                  <option value="oldest">Oldest first seen</option>
                </select>
                <select
                  value={invalidLimit}
                  onChange={(e) => {
                    setInvalidLimit(parseInt(e.target.value, 10));
                    setInvalidPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                >
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
            </div>

            {invalidError && <div className="px-4 py-3 text-sm text-red-600">{invalidError}</div>}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Player</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Times rejected</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">First seen (CST)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last seen (CST)</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invalidLoading && invalidRows.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td>
                    </tr>
                  ) : invalidRows.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-10 text-center text-sm text-gray-500">
                        No invalid or unusable numbers captured yet.
                      </td>
                    </tr>
                  ) : (
                    invalidRows.map((row) => (
                      <tr key={row.id || row.phone} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          <div className="font-medium">{row.phone}</div>
                          {row.original_phone && row.original_phone !== row.phone && (
                            <div className="text-xs text-gray-500">{row.original_phone}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="font-medium">{row.player_name || '—'}</div>
                          <div className="text-xs text-gray-500">
                            {row.player_number ? `#${row.player_number}` : ''}
                            {row.player_id ? ` • ID ${row.player_id}` : ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {reasonBadge(row.reason)}
                          {row.error_message && (
                            <div className="mt-1 text-xs text-gray-500 max-w-[280px] line-clamp-2">{row.error_message}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.hit_count || 1}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {row.first_seen_at ? (
                            <div title={`${moment(row.first_seen_at).tz(CST_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')} CST`}>
                              <div className="font-medium text-gray-900">
                                {moment(row.first_seen_at).tz(CST_TIMEZONE).format('MMM DD, YYYY')}
                              </div>
                              <div className="text-xs text-gray-500 font-mono">
                                {moment(row.first_seen_at).tz(CST_TIMEZONE).format('h:mm A')} CST
                              </div>
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {row.last_seen_at ? (
                            <div title={`${moment(row.last_seen_at).tz(CST_TIMEZONE).format('YYYY-MM-DD HH:mm:ss')} CST`}>
                              <div className="font-medium text-gray-900">
                                {moment(row.last_seen_at).tz(CST_TIMEZONE).format('MMM DD, YYYY')}
                              </div>
                              <div className="text-xs text-gray-500 font-mono">
                                {moment(row.last_seen_at).tz(CST_TIMEZONE).format('h:mm A')} CST
                              </div>
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleUnblock(row)}
                            disabled={unblockingId === row.id}
                            className="inline-flex items-center px-2.5 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded hover:bg-emerald-100 disabled:opacity-50 transition-colors shadow-sm"
                            title="Unblock number to allow messaging again"
                          >
                            {unblockingId === row.id ? 'Unblocking...' : 'Unblock / Opt-in'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={invalidPage}
              totalPages={invalidTotalPages}
              total={invalidTotal}
              loading={invalidLoading}
              onPage={setInvalidPage}
            />
          </div>
        </>
      )}

      {/* Bulk Messaging Modal */}
      <BulkSmsMessaging
        isOpen={showBulkMessagingModal}
        onClose={() => {
          setShowBulkMessagingModal(false);
          fetchLogs();
        }}
        loadPlayers={loadSmsPlayers}
        sendAll={sendAllActiveConfig}
        activePlatform={activePlatform}
        gatewayBalance={currentBalance}
        onSwitchPlatform={handleSwitchPlatform}
        onSchedule={(msg, recips, targetType) => {
          setScheduledInitialMessage(msg);
          setScheduledRecipients(recips);
          setScheduledTargetType(targetType || (recips.length > 0 ? 'custom' : 'all_active'));
          setShowBulkMessagingModal(false);
          setShowScheduledSmsModal(true);
        }}
      />

      {/* Scheduled SMS Modal */}
      <ScheduledSmsModal
        isOpen={showScheduledSmsModal}
        onClose={() => {
          setShowScheduledSmsModal(false);
          fetchLogs();
        }}
        initialMessage={scheduledInitialMessage}
        targetType={scheduledTargetType}
        selectedRecipients={scheduledRecipients}
        activePlatform={activePlatform}
        gatewayBalance={currentBalance}
        onSwitchPlatform={handleSwitchPlatform}
        onOpenMessaging={() => {
          setShowScheduledSmsModal(false);
          setShowBulkMessagingModal(true);
        }}
      />

      {/* Unblock Confirmation Modal */}
      {unblockConfirmTarget && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-emerald-100 animate-in zoom-in-95 duration-150">
            <div className="px-6 py-4 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Unblock Phone Number</h3>
                  <p className="text-xs text-emerald-700">Re-enable player for SMS broadcasts</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setUnblockConfirmTarget(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-3 text-xs">
              <p className="text-gray-700 text-sm">
                Are you sure you want to unblock <strong>{unblockConfirmTarget.phone}</strong>?
              </p>
              {unblockConfirmTarget.player_name && (
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <span className="text-gray-500">Player: </span>
                  <span className="font-semibold text-gray-900">{unblockConfirmTarget.player_name}</span>
                  {unblockConfirmTarget.player_number && (
                    <span className="text-gray-500 font-mono text-xs ml-1.5">#{unblockConfirmTarget.player_number}</span>
                  )}
                </div>
              )}
              <p className="text-gray-500">
                This number will be removed from the suppression list and will be eligible to receive future SMS broadcasts.
              </p>
            </div>
            <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-200 flex justify-end gap-2.5">
              <button
                type="button"
                disabled={unblockingId === unblockConfirmTarget.id}
                onClick={() => setUnblockConfirmTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 bg-white hover:bg-gray-100 transition-colors shadow-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={unblockingId === unblockConfirmTarget.id}
                onClick={confirmUnblock}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 transition-colors shadow-sm disabled:opacity-50"
              >
                {unblockingId === unblockConfirmTarget.id ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    <span>Unblocking...</span>
                  </>
                ) : (
                  <span>Confirm Unblock</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsLogsPage;
